import * as Api from '@ViewModels';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../web/ReactQueryProvider';

interface Params {
	month: string;
	request: Api.IContentCalendarSuggestionRequest;
	impersonationContext?: Api.IImpersonationContext;
	enabled?: boolean;
}

const BASE_QUERY_KEY = 'ContentCalendarSuggestion/filter';

const fetchSuggestions = (userSession: ReturnType<typeof useUserSession>, params: Omit<Params, 'enabled'>) => {
	return userSession.webServiceHelper.callAsync<Api.IContentCalendarSuggestion[]>(
		Api.ImpersonationBroker.composeApiUrl({
			urlPath: `ContentCalendarSuggestion/filter`,
			queryParams: {
				month: params.month,
			},
			impersonationContext: params.impersonationContext,
		}),
		'POST',
		params.request
	);
};

const buildQueryKey = (params: Omit<Params, 'enabled'>) => [
	BASE_QUERY_KEY,
	params.month,
	params.request,
	params.impersonationContext,
];

export const useContentCalendarSuggestionFilterQuery = (params: Params) => {
	const userSession = useUserSession();
	return useQuery({
		enabled: params.enabled,
		queryFn: () => fetchSuggestions(userSession, params),
		queryKey: buildQueryKey(params),
	});
};

export const useContentCalendarSuggestionFilterQueries = (params: Params[]) => {
	const userSession = useUserSession();
	return useQueries({
		queries: params.map(p => {
			return {
				enabled: p.enabled,
				queryFn: () => fetchSuggestions(userSession, p),
				queryKey: buildQueryKey(p),
			};
		}),
	});
};

/**
 * Imperatively fetches suggestions for months that have no rendered query (e.g. auto-select needs
 * the months beyond the visible quarter). Uses the same query keys as the hooks above, so results
 * land in the cache and are reused when the user pages the calendar to those months.
 */
export const useFetchContentCalendarSuggestionFilterQueries = () => {
	const userSession = useUserSession();
	return useCallback(
		async (params: Omit<Params, 'enabled'>[]) => {
			// allSettled, not all: a transient failure on one month must not discard the months that
			// resolved fine — callers get every fulfilled month's suggestions.
			const results = await Promise.allSettled(
				params.map(p =>
					reactQueryClient.fetchQuery({
						queryFn: () => fetchSuggestions(userSession, p),
						queryKey: buildQueryKey(p),
						// Reuse months the rendered queries fetched moments ago (the auto-select scan
						// overlaps the visible quarter). Invalidation still wins: fetchQuery refetches
						// invalidated entries regardless of staleTime.
						staleTime: 30 * 1000,
					})
				)
			);
			return results
				.filter((result): result is PromiseFulfilledResult<Api.IContentCalendarSuggestion[]> => {
					return result.status === 'fulfilled';
				})
				.flatMap(result => result.value);
		},
		[userSession]
	);
};

export const invalidateAllContentCalendarSuggestions = () => {
	reactQueryClient.invalidateQueries({ queryKey: [BASE_QUERY_KEY] });
};
